import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useAgency } from '@/contexts/AgencyContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Loader2 } from 'lucide-react';
import { FlowTokLogo } from '@/components/FlowTokLogo';
import { resolveLandingRoute } from '@/lib/auth-landing';
import { SEO } from '@/components/SEO';
import { logAuthEvent } from '@/lib/auth-telemetry';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resetSent, setResetSent] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetCooldown, setResetCooldown] = useState(0);
  const { signIn, user, role, loading: authLoading, resetPassword } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const reasonParam = searchParams.get('reason');
  const redirectParam = searchParams.get('redirect');
  const agency = useAgency();
  const authStartedAt = useRef<number | null>(null);
  const redirectStartedAt = useRef<{ at: number; target: string } | null>(null);

  useEffect(() => {
    if (resetCooldown <= 0) return;
    const t = setInterval(() => setResetCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [resetCooldown]);

  // Once both auth + agency context are loaded, send the user to the canonical
  // landing route. ProtectedRoute uses the same resolver so there's no bounce.
  useEffect(() => {
    if (authLoading || agency.loading || !user) return;
    const defaultRoute = resolveLandingRoute({
      user,
      role,
      isSuperAdmin: agency.isSuperAdmin,
      agencyId: agency.agencyId,
      agencyRole: agency.agencyRole,
      onboardingComplete: agency.onboardingComplete,
      approvalStatus: agency.approvalStatus,
      agencyApprovalState: agency.agencyApprovalState,
    });

    // Only honour ?redirect= when the path is in the same role-zone as the
    // user's natural landing route. Otherwise we'd send a brand_client to
    // /dashboard, ProtectedRoute would deny, and AccessDeniedWatcher would
    // flash a destructive toast right after a successful sign-in.
    const zoneOf = (p: string) => {
      if (p.startsWith('/superadmin')) return 'platform';
      if (p.startsWith('/portal')) return 'brand';
      if (p.startsWith('/creator')) return 'creator';
      if (p.startsWith('/onboarding')) return 'onboarding';
      return 'agency';
    };
    const safeRedirect =
      redirectParam && redirectParam.startsWith('/') && !redirectParam.startsWith('//')
        ? redirectParam
        : null;
    const target =
      safeRedirect && zoneOf(safeRedirect) === zoneOf(defaultRoute)
        ? safeRedirect
        : defaultRoute;

    console.info('[auth] redirect_started', { target, role, isSuperAdmin: agency.isSuperAdmin });
    redirectStartedAt.current = { at: performance.now(), target };
    void logAuthEvent({
      event: 'redirect_started',
      success: true,
      userId: user.id,
      email: user.email ?? null,
      role: role ?? null,
      landingRoute: target,
    });
    navigate(target, { replace: true });
  }, [user, role, authLoading, agency.loading, agency.isSuperAdmin, agency.agencyId, agency.agencyRole, agency.onboardingComplete, agency.approvalStatus, navigate, redirectParam]);

  // Fire redirect_completed once we're no longer rendering /login.
  useEffect(() => {
    const started = redirectStartedAt.current;
    if (started && location.pathname !== '/login') {
      void logAuthEvent({
        event: 'redirect_completed',
        success: true,
        userId: user?.id ?? null,
        email: user?.email ?? null,
        role: role ?? null,
        landingRoute: location.pathname,
        durationMs: Math.round(performance.now() - started.at),
        metadata: { intended: started.target },
      });
      redirectStartedAt.current = null;
    }
  }, [location.pathname, user, role]);

  if (authLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center" style={{ background: '#F8F9FC' }}>
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (user) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    console.info('[auth] auth_started', { email });
    authStartedAt.current = performance.now();
    void logAuthEvent({ event: 'auth_started', email });
    const { error } = await signIn(email, password);
    const dur = authStartedAt.current ? Math.round(performance.now() - authStartedAt.current) : null;
    if (error) {
      console.warn('[auth] auth_failed', { message: error.message });
      void logAuthEvent({
        event: 'auth_failed',
        success: false,
        email,
        errorMessage: error.message,
        durationMs: dur,
      });
      setLoading(false);
      setError('Invalid email or password. Please try again.');
      return;
    }
    console.info('[auth] auth_success');
    void logAuthEvent({
      event: 'auth_success',
      success: true,
      email,
      durationMs: dur,
    });
  };

  const handleForgotPassword = async () => {
    if (!email) {
      setError('Enter your email address above, then click "Forgot your password?"');
      return;
    }
    if (resetCooldown > 0 || resetLoading) return;
    setError('');
    setResetLoading(true);
    const { error } = await resetPassword(email);
    setResetLoading(false);
    if (error) {
      setError(error.message);
    } else {
      setResetSent(true);
      setResetCooldown(30);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center px-5" style={{ background: '#F8F9FC' }}>
      <SEO
        title="Sign in — FlowTokOS"
        description="Sign in to FlowTokOS to run your TikTok Shop agency: creators, LIVE commerce, campaigns, forecasting and approvals."
        path="/login"
        noindex
      />
      <div className="w-full max-w-[420px]">
        <div className="p-8 rounded-2xl" style={{ background: '#FFFFFF', border: '1px solid #E8EDF2' }}>
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex flex-col items-center hover:opacity-80 transition-opacity">
              <FlowTokLogo size={56} />
              <p className="text-xs mt-2" style={{ color: '#6B7280' }}>
                The Operating System for TikTok Shop & Live Commerce
              </p>
            </Link>
          </div>

          {reasonParam === 'session_revoked' && (
            <div className="mb-5 rounded-lg p-3 text-xs" style={{ background: '#FFF7ED', border: '1px solid #FED7AA', color: '#9A3412' }}>
              Your session was ended by a platform admin. Sign in again to continue.
            </div>
          )}
          {reasonParam === 'session_expired' && (
            <div className="mb-5 rounded-lg p-3 text-xs" style={{ background: '#F0F9FF', border: '1px solid #BAE6FD', color: '#075985' }}>
              Your session expired. Sign in to pick up where you left off.
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="text-[10px] uppercase tracking-wider font-semibold block mb-1.5" style={{ color: '#6B7280' }}>
                Email Address
              </label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@agency.com"
                required
                className="text-sm"
                style={{ borderColor: '#E8EDF2' }}
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-semibold block mb-1.5" style={{ color: '#6B7280' }}>
                Password
              </label>
              <PasswordInput
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                className="text-sm"
                style={{ borderColor: '#E8EDF2' }}
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full text-sm text-white font-semibold h-11"
              style={{ background: '#1E8FE1' }}
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Signing in...
                </>
              ) : (
                'Sign In →'
              )}
            </Button>

            {error && (
              <p className="text-xs text-center" style={{ color: '#DC2626' }}>{error}</p>
            )}

            {resetSent && (
              <div
                className="rounded-lg p-3 text-xs space-y-2"
                style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', color: '#166534' }}
              >
                <p className="font-medium">Password reset email sent.</p>
                <p style={{ color: '#15803D' }}>
                  Please check your inbox and spam folder. Emails usually arrive within a few seconds.
                </p>
              </div>
            )}
          </form>

          <div className="mt-6 space-y-4">
            <button
              type="button"
              onClick={handleForgotPassword}
              disabled={resetLoading || resetCooldown > 0}
              className="w-full text-xs text-center hover:underline disabled:no-underline disabled:opacity-60"
              style={{ color: '#1E8FE1' }}
            >
              {resetLoading
                ? 'Sending reset email…'
                : resetCooldown > 0
                ? `Resend password reset (${resetCooldown}s)`
                : resetSent
                ? 'Resend password reset email'
                : 'Forgot your password?'}
            </button>


            <div className="flex items-center gap-3">
              <div className="flex-1 h-px" style={{ background: '#E8EDF2' }} />
              <span className="text-[10px] uppercase tracking-wider" style={{ color: '#A3A3A3' }}>or</span>
              <div className="flex-1 h-px" style={{ background: '#E8EDF2' }} />
            </div>

            <div className="flex flex-col items-center gap-3 text-xs">
              <p style={{ color: '#6B7280' }}>Don't have access yet?</p>
              <div className="flex gap-2 w-full">
                <Link to="/book-demo" className="flex-1">
                  <button type="button" className="w-full rounded-md border px-3 py-2 text-xs font-semibold hover:bg-[#F8FAFC]" style={{ borderColor: '#1E8FE1', color: '#1E8FE1' }}>
                    Request Access
                  </button>
                </Link>
                <Link to="/book-demo" className="flex-1">
                  <button type="button" className="w-full rounded-md px-3 py-2 text-xs font-semibold text-white hover:opacity-90" style={{ background: '#1E8FE1' }}>
                    Book a Demo
                  </button>
                </Link>
              </div>
              <Link to="/" className="hover:underline" style={{ color: '#6B7280' }}>
                Back to home
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
